import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  NOTICE_SEND_POLICY_LABEL,
  PURPOSE_LABEL,
  type CampaignChannelKind,
  type CampaignPurpose,
  type ChatbotInstance,
  type NoticeSendPolicy,
  type WhatsAppChannel,
  type WhatsAppTemplate,
} from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { hasModuleFeature } from '@/features/auth/moduleFeatures';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { whatsappAdminApi } from '@/features/whatsapp/api/whatsappApi';
import { campaignChannelOptions } from '../api/campaignChannelAvailability';
import { CAMPAIGNS_HUB_PATH, noticeProgramDetailPath } from '../api/campaignPaths';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import {
  canSaveNoticeDraft,
  contactoChannelsForInstance,
  noticeDraftBlockReason,
  pickChannelId,
} from '../api/noticeProgramChannels';
import {
  noticeTemplateSlots,
  noticeVariablesReady,
  uniqueNoticeTemplates,
} from '../api/noticeTemplateVariables';
import { parseCampaignChannelKind } from '../api/setupEventChannel';
import { CampaignWizardAxisCards } from '../components/CampaignWizardAxisCards';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { MessageTemplatePicker } from '../components/MessageTemplatePicker';
import { NoticeTemplateVariableFields } from '../components/NoticeTemplateVariableFields';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

export function NoticeProgramNewPage() {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [searchParams] = useSearchParams();
  const requestedKind = parseCampaignChannelKind(searchParams.get('channelKind'));
  const [name, setName] = useState('Aviso de entrega hoy');
  const [purpose, setPurpose] = useState<CampaignPurpose>('AVISO');
  const [channelKind, setChannelKind] = useState<CampaignChannelKind>(requestedKind ?? 'WHATSAPP');
  const [sendPolicy, setSendPolicy] = useState<NoticeSendPolicy>('IMMEDIATE');
  const [instances, setInstances] = useState<ChatbotInstance[]>([]);
  const [instanceId, setInstanceId] = useState('');
  const [channels, setChannels] = useState<WhatsAppChannel[]>([]);
  const [whatsAppChannelId, setWhatsAppChannelId] = useState('');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [whatsAppTemplateName, setWhatsAppTemplateName] = useState('');
  const [templateVariables, setTemplateVariables] = useState<string[]>([]);
  const [messageTemplateId, setMessageTemplateId] = useState('');
  const [maxPerContactPerDay, setMaxPerContactPerDay] = useState(5);
  const [quietFrom, setQuietFrom] = useState('');
  const [quietTo, setQuietTo] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const tenantHasSms = hasModuleFeature(currentUser, 'sms-integration');
  const tenantHasEmail = hasModuleFeature(currentUser, 'email-integration');
  const selectedInstance = instances.find(item => item.id === instanceId);
  const instanceChannels = useMemo(
    () => contactoChannelsForInstance(channels, instanceId),
    [channels, instanceId],
  );
  const channelOptions = useMemo(
    () =>
      campaignChannelOptions({
        deliveryMode: 'NOTIFICATION',
        tenantHasSms,
        tenantHasEmail,
        instanceChannels: selectedInstance?.enabledOutboundChannels ?? ['WHATSAPP'],
      }),
    [selectedInstance, tenantHasEmail, tenantHasSms],
  );
  const templateOptions = useMemo(() => uniqueNoticeTemplates(templates), [templates]);
  const variableSlots =
    channelKind === 'WHATSAPP'
      ? noticeTemplateSlots(templateOptions.find(template => template.name === whatsAppTemplateName))
      : 0;
  const variablesReady = noticeVariablesReady(variableSlots, templateVariables, purpose);
  const canSave =
    variablesReady &&
    canSaveNoticeDraft({
      instanceId,
      channelKind,
      whatsAppChannelId,
      whatsAppTemplateName,
      messageTemplateId,
    });

  useEffect(() => {
    if (!ready) {
      return;
    }
    void Promise.all([
      chatbotAdminApi.listInstances(tenantId).catch(() => [] as ChatbotInstance[]),
      whatsappAdminApi.listChannels(tenantId).catch(() => [] as WhatsAppChannel[]),
    ]).then(([instanceList, channelList]) => {
      setInstances(instanceList);
      setChannels(channelList);
      setInstanceId(current => current || instanceList[0]?.id || '');
    });
  }, [ready, tenantId]);

  useEffect(() => {
    setWhatsAppChannelId(current => pickChannelId(instanceChannels, current));
  }, [instanceChannels]);

  useEffect(() => {
    if (!whatsAppChannelId || channelKind !== 'WHATSAPP') {
      setTemplates([]);
      return;
    }
    void whatsappAdminApi
      .listTemplates(whatsAppChannelId)
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [whatsAppChannelId, channelKind]);

  useEffect(() => {
    // Otra plantilla u otro proposito: los datos elegidos antes ya no corresponden.
    setTemplateVariables([]);
  }, [whatsAppTemplateName, purpose]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) {
      setError(
        variablesReady
          ? noticeDraftBlockReason({ channelKind, whatsAppChannelId })
          : 'Elegí qué dato va en cada variable de la plantilla',
      );
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await contactAgentApi.createNoticeProgram({
        name: name.slice(0, 255),
        purpose,
        channelKind,
        instanceId,
        whatsAppChannelId: channelKind === 'WHATSAPP' ? whatsAppChannelId || undefined : undefined,
        whatsAppTemplateName: channelKind === 'WHATSAPP' ? whatsAppTemplateName || undefined : undefined,
        whatsAppTemplateVariables:
          variableSlots > 0 ? templateVariables.slice(0, variableSlots) : undefined,
        messageTemplateId: channelKind === 'WHATSAPP' ? undefined : messageTemplateId || undefined,
        sendPolicy,
        quietFrom: quietFrom || null,
        quietTo: quietTo || null,
        maxPerContactPerDay,
      });
      navigate(noticeProgramDetailPath(saved.id));
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        backTo={`${CAMPAIGNS_HUB_PATH}?tab=dispatches`}
        title='Nuevo disparo'
        description='Tu sistema avisa; acá solo se configura el mensaje y el canal.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />
      <form onSubmit={event => void onSubmit(event)} className='space-y-6'>
        <Card className='rounded-xl'>
          <CardHeader>
            <CardTitle>Programa</CardTitle>
          </CardHeader>
          <CardContent className='grid gap-6 md:grid-cols-2'>
            <div className='md:col-span-2 space-y-2'>
              <Label htmlFor='notice-name'>Nombre</Label>
              <Input
                id='notice-name'
                value={name}
                maxLength={255}
                onChange={event => setName(event.target.value.slice(0, 255))}
              />
            </div>
            <CampaignWizardAxisCards
              legend='Propósito'
              value={purpose}
              onChange={setPurpose}
              options={[
                { id: 'AVISO', title: PURPOSE_LABEL.AVISO, description: 'Paquete, cita o recordatorio.' },
                { id: 'COBRANZAS', title: PURPOSE_LABEL.COBRANZAS, description: 'Un aviso de vencimiento puntual.' },
              ]}
            />
            <CampaignWizardAxisCards
              legend='Canal'
              value={channelKind}
              onChange={setChannelKind}
              options={channelOptions.map(option => ({
                id: option.kind,
                title: option.label,
                description: option.reason ?? 'Un canal por programa.',
                enabled: option.enabled,
                reason: option.reason,
              }))}
            />
            <div className='space-y-2'>
              <Label htmlFor='notice-instance'>Instancia</Label>
              <select
                id='notice-instance'
                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                value={instanceId}
                onChange={event => setInstanceId(event.target.value)}
              >
                <option value=''>Elegi una instancia</option>
                {instances.map(instance => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name}
                  </option>
                ))}
              </select>
            </div>
            {channelKind === 'WHATSAPP' ? (
              <>
                <div className='space-y-2'>
                  <Label htmlFor='notice-wa-channel'>Canal de WhatsApp</Label>
                  <select
                    id='notice-wa-channel'
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                    value={whatsAppChannelId}
                    onChange={event => setWhatsAppChannelId(event.target.value)}
                  >
                    <option value=''>
                      {instanceChannels.length === 0
                        ? 'No hay canal de contacto en esta instancia'
                        : 'Elegi un canal'}
                    </option>
                    {instanceChannels.map(channel => (
                      <option key={channel.id} value={channel.id}>
                        {channel.displayPhoneNumber}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='space-y-2 md:col-span-2'>
                  <Label htmlFor='notice-wa-template'>Plantilla de WhatsApp</Label>
                  <select
                    id='notice-wa-template'
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                    value={whatsAppTemplateName}
                    onChange={event => setWhatsAppTemplateName(event.target.value)}
                  >
                    <option value=''>Elegi una plantilla</option>
                    {templateOptions.map(template => (
                      <option key={template.id} value={template.name}>
                        {template.status === 'APPROVED'
                          ? template.name
                          : `${template.name} (sin aprobar por Meta)`}
                      </option>
                    ))}
                  </select>
                </div>
                <NoticeTemplateVariableFields
                  slots={variableSlots}
                  purpose={purpose}
                  value={templateVariables}
                  onChange={setTemplateVariables}
                />
              </>
            ) : (
              <div className='md:col-span-2'>
                <MessageTemplatePicker
                  channelKind={channelKind}
                  value={messageTemplateId}
                  onChange={setMessageTemplateId}
                  onPersistWizard={() => undefined}
                />
              </div>
            )}
            <CampaignWizardAxisCards
              legend='Cuando sale'
              value={sendPolicy}
              onChange={setSendPolicy}
              options={[
                {
                  id: 'IMMEDIATE',
                  title: NOTICE_SEND_POLICY_LABEL.IMMEDIATE,
                  description: 'El evento dispara el aviso al toque.',
                },
                {
                  id: 'CAMPAIGN_WINDOW',
                  title: NOTICE_SEND_POLICY_LABEL.CAMPAIGN_WINDOW,
                  description: 'Espera el horario laboral 9 a 18.',
                },
              ]}
            />
            <div className='space-y-2'>
              <Label htmlFor='notice-cap'>Tope por contacto y día</Label>
              <Input
                id='notice-cap'
                type='number'
                min={1}
                max={50}
                value={maxPerContactPerDay}
                onChange={event => setMaxPerContactPerDay(Number(event.target.value) || 1)}
              />
            </div>
            <div className='md:col-span-2'>
              <Button type='button' variant='ghost' onClick={() => setAdvanced(value => !value)}>
                {advanced ? 'Ocultar opciones' : 'Más opciones'}
              </Button>
            </div>
            {advanced ? (
              <>
                <div className='space-y-2'>
                  <Label htmlFor='quiet-from'>Silencio desde</Label>
                  <Input id='quiet-from' type='time' value={quietFrom} onChange={event => setQuietFrom(event.target.value)} />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='quiet-to'>Silencio hasta</Label>
                  <Input id='quiet-to' type='time' value={quietTo} onChange={event => setQuietTo(event.target.value)} />
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
        {error ? <p className='text-destructive'>{error}</p> : null}
        <Button type='submit' disabled={saving || !canSave}>
          {saving ? 'Guardando...' : 'Guardar borrador'}
        </Button>
      </form>
    </ContactAgentPage>
  );
}
